import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { calendarApi, reminderApi, leadApi, projectApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Plus, Bell, Clock, Trash2, MapPin, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import '../styles/calendar.css';

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales: { 'en-US': enUS },
});
const DnDCalendar = withDragAndDrop(Calendar);

export const EVENT_TYPES = {
  fieldwork: { label: 'Fieldwork', color: '#f97316' },
  site_visit: { label: 'Site Visit', color: '#2563eb' },
  client_meeting: { label: 'Client Meeting', color: '#16a34a' },
  internal: { label: 'Internal / Team', color: '#7c3aed' },
  other: { label: 'Other', color: '#64748b' },
};

const fmtInput = (d) => format(d, "yyyy-MM-dd'T'HH:mm");
const fmtStore = (d) => format(d, "yyyy-MM-dd'T'HH:mm:ss");

const emptyForm = () => ({
  title: '',
  description: '',
  start_time: '',
  end_time: '',
  location: '',
  event_type: 'fieldwork',
  status: 'confirmed',
  all_day: false,
  lead_id: '',
  project_id: '',
  sync_to_google: false,
});

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [leads, setLeads] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('week');
  const [date, setDate] = useState(new Date());
  const [activeTypes, setActiveTypes] = useState(
    Object.keys(EVENT_TYPES).reduce((a, k) => ({ ...a, [k]: true }), {})
  );

  const [showEventDialog, setShowEventDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [newReminder, setNewReminder] = useState({
    title: '', description: '', due_date: '', reminder_type: 'follow_up', lead_id: '', project_id: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, remindersRes, leadsRes, projectsRes] = await Promise.all([
        calendarApi.getEvents(),
        reminderApi.getAll({ completed: false }),
        leadApi.getAll(),
        projectApi.getAll(),
      ]);
      setEvents(eventsRes.data);
      setReminders(remindersRes.data);
      setLeads(leadsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  // Map backend events -> react-big-calendar events, filtered by active types
  const rbcEvents = useMemo(() => {
    return events
      .filter((e) => activeTypes[e.event_type || 'fieldwork'])
      .map((e) => ({
        id: e.id,
        title: e.title,
        start: new Date(e.start_time),
        end: new Date(e.end_time),
        allDay: !!e.all_day,
        resource: e,
      }));
  }, [events, activeTypes]);

  const eventPropGetter = useCallback((event) => {
    const cfg = EVENT_TYPES[event.resource?.event_type] || EVENT_TYPES.other;
    const tentative = event.resource?.status === 'tentative';
    return {
      style: {
        backgroundColor: cfg.color,
        color: '#fff',
        borderRadius: '4px',
        border: tentative ? `2px dashed rgba(255,255,255,0.7)` : 'none',
        opacity: tentative ? 0.75 : 1,
      },
    };
  }, []);

  const EventChip = useCallback(({ event }) => (
    <span title={event.title} className="block truncate leading-tight">
      {event.title}
    </span>
  ), []);

  // Drag-drop / resize reschedule
  const persistTimes = async (event, start, end) => {
    setEvents((prev) =>
      prev.map((e) => (e.id === event.id ? { ...e, start_time: fmtStore(start), end_time: fmtStore(end) } : e))
    );
    try {
      await calendarApi.updateEvent(event.id, {
        start_time: fmtStore(start),
        end_time: fmtStore(end),
        all_day: event.allDay,
      });
      toast.success('Event rescheduled');
    } catch (error) {
      toast.error('Failed to reschedule');
      fetchData();
    }
  };

  const handleSelectSlot = ({ start, end }) => {
    setEditingId(null);
    setForm({ ...emptyForm(), start_time: fmtInput(start), end_time: fmtInput(end) });
    setShowEventDialog(true);
  };

  const handleSelectEvent = (event) => {
    const e = event.resource;
    setEditingId(e.id);
    setForm({
      title: e.title || '',
      description: e.description || '',
      start_time: fmtInput(new Date(e.start_time)),
      end_time: fmtInput(new Date(e.end_time)),
      location: e.location || '',
      event_type: e.event_type || 'fieldwork',
      status: e.status || 'confirmed',
      all_day: !!e.all_day,
      lead_id: e.lead_id || '',
      project_id: e.project_id || '',
      sync_to_google: !!e.sync_to_google,
    });
    setShowEventDialog(true);
  };

  const openNewEvent = () => {
    const start = new Date();
    start.setMinutes(0, 0, 0);
    const end = new Date(start);
    end.setHours(end.getHours() + 1);
    setEditingId(null);
    setForm({ ...emptyForm(), start_time: fmtInput(start), end_time: fmtInput(end) });
    setShowEventDialog(true);
  };

  const handleSaveEvent = async () => {
    if (!form.title || !form.start_time || !form.end_time) {
      toast.error('Title, start and end are required');
      return;
    }
    const payload = {
      ...form,
      lead_id: form.lead_id || null,
      project_id: form.project_id || null,
    };
    try {
      if (editingId) {
        await calendarApi.updateEvent(editingId, payload);
        toast.success('Event updated');
      } else {
        await calendarApi.createEvent(payload);
        toast.success('Event created');
      }
      setShowEventDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleDeleteEvent = async () => {
    if (!editingId) return;
    if (!window.confirm('Delete this event?')) return;
    try {
      await calendarApi.deleteEvent(editingId);
      setShowEventDialog(false);
      setEvents((prev) => prev.filter((e) => e.id !== editingId));
      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleCreateReminder = async () => {
    if (!newReminder.title || !newReminder.due_date) {
      toast.error('Title and due date are required');
      return;
    }
    try {
      await reminderApi.create(newReminder);
      setShowReminderDialog(false);
      setNewReminder({ title: '', description: '', due_date: '', reminder_type: 'follow_up', lead_id: '', project_id: '' });
      fetchData();
      toast.success('Reminder created');
    } catch (error) {
      toast.error('Failed to create reminder');
    }
  };

  const handleCompleteReminder = async (id) => {
    try {
      await reminderApi.complete(id);
      setReminders((prev) => prev.filter((r) => r.id !== id));
      toast.success('Reminder completed');
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <span className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Schedule</h1>
          <p className="text-muted-foreground">Plan your fieldwork, site visits and client meetings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={() => setShowReminderDialog(true)} data-testid="new-reminder-btn">
            <Bell className="h-4 w-4 mr-2" />
            New Reminder
          </Button>
          <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" onClick={openNewEvent} data-testid="new-event-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Event
          </Button>
        </div>
      </div>

      {/* Type filter chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="event-type-filters">
        <span className="text-sm text-muted-foreground mr-1">Show:</span>
        {Object.entries(EVENT_TYPES).map(([key, cfg]) => {
          const active = activeTypes[key];
          return (
            <button
              key={key}
              onClick={() => setActiveTypes((p) => ({ ...p, [key]: !p[key] }))}
              data-testid={`filter-${key}`}
              className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border transition-all ${
                active ? 'text-white' : 'text-muted-foreground bg-transparent'
              }`}
              style={active ? { backgroundColor: cfg.color, borderColor: cfg.color } : { borderColor: '#e2e8f0' }}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: active ? '#fff' : cfg.color }} />
              {cfg.label}
            </button>
          );
        })}
      </div>

      {/* Calendar */}
      <Card className="rounded-sm">
        <CardContent className="p-3 sm:p-4">
          <div style={{ height: 680 }} data-testid="big-calendar">
            <DnDCalendar
              localizer={localizer}
              events={rbcEvents}
              startAccessor="start"
              endAccessor="end"
              views={['month', 'week', 'day', 'agenda']}
              view={view}
              onView={setView}
              date={date}
              onNavigate={setDate}
              selectable
              popup
              step={30}
              timeslots={2}
              scrollToTime={new Date(1970, 1, 1, 7)}
              onSelectSlot={handleSelectSlot}
              onSelectEvent={handleSelectEvent}
              onEventDrop={({ event, start, end }) => persistTimes(event, start, end)}
              onEventResize={({ event, start, end }) => persistTimes(event, start, end)}
              eventPropGetter={eventPropGetter}
              components={{ event: EventChip }}
              resizable
            />
          </div>
        </CardContent>
      </Card>

      {/* Reminders */}
      <Card className="rounded-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="h-5 w-5 text-orange-500" />
            Reminders & Follow-ups
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reminders.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground text-sm">No pending reminders</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {reminders.map((reminder) => (
                <div key={reminder.id} className="flex items-start gap-3 p-3 bg-secondary/50 rounded-sm" data-testid={`reminder-${reminder.id}`}>
                  <Checkbox onCheckedChange={() => handleCompleteReminder(reminder.id)} className="mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{reminder.title}</p>
                    {reminder.description && <p className="text-xs text-muted-foreground mt-1">{reminder.description}</p>}
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                      <Clock className="h-3 w-3" />
                      {new Date(reminder.due_date).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                  <Badge variant="outline" className="text-xs">{reminder.reminder_type.replace('_', ' ')}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event create/edit dialog */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarCheck className="h-5 w-5" />
              {editingId ? 'Edit Event' : 'New Event'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Title *</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Site visit with client"
                className="rounded-sm mt-1"
                data-testid="event-title-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={form.event_type} onValueChange={(v) => setForm({ ...form, event_type: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="event-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(EVENT_TYPES).map(([key, cfg]) => (
                      <SelectItem key={key} value={key}>
                        <span className="flex items-center gap-2">
                          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: cfg.color }} />
                          {cfg.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="event-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="tentative">Tentative</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start *</Label>
                <Input type="datetime-local" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="rounded-sm mt-1" data-testid="event-start-input" />
              </div>
              <div>
                <Label>End *</Label>
                <Input type="datetime-local" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="rounded-sm mt-1" data-testid="event-end-input" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={form.all_day} onCheckedChange={(c) => setForm({ ...form, all_day: !!c })} data-testid="event-allday-check" />
              <Label className="text-sm">All day</Label>
            </div>
            <div>
              <Label>Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="123 Main St" className="rounded-sm mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Link to Lead</Label>
                <Select value={form.lead_id || 'none'} onValueChange={(v) => setForm({ ...form, lead_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {leads.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Link to Project</Label>
                <Select value={form.project_id || 'none'} onValueChange={(v) => setForm({ ...form, project_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="rounded-sm mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notes..." className="rounded-sm mt-1" rows={2} />
            </div>
            <div className="flex items-center gap-2 p-3 bg-secondary/40 rounded-sm">
              <Checkbox checked={form.sync_to_google} onCheckedChange={(c) => setForm({ ...form, sync_to_google: !!c })} data-testid="event-sync-google-check" />
              <Label className="text-sm">Push to my Google Calendar (only mine — never teammates')</Label>
            </div>
          </div>
          <DialogFooter className="mt-4 flex sm:justify-between gap-2">
            {editingId ? (
              <Button variant="outline" className="rounded-sm text-red-600" onClick={handleDeleteEvent} data-testid="delete-event-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Delete
              </Button>
            ) : <span />}
            <Button onClick={handleSaveEvent} className="rounded-sm" data-testid="save-event-btn">
              {editingId ? 'Save Changes' : 'Create Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reminder dialog */}
      <Dialog open={showReminderDialog} onOpenChange={setShowReminderDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Reminder</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Title *</Label>
              <Input value={newReminder.title} onChange={(e) => setNewReminder({ ...newReminder, title: e.target.value })} placeholder="Follow up with John" className="rounded-sm mt-1" data-testid="reminder-title-input" />
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input type="datetime-local" value={newReminder.due_date} onChange={(e) => setNewReminder({ ...newReminder, due_date: e.target.value })} className="rounded-sm mt-1" data-testid="reminder-due-input" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={newReminder.reminder_type} onValueChange={(v) => setNewReminder({ ...newReminder, reminder_type: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="appointment">Appointment</SelectItem>
                  <SelectItem value="task">Task</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={newReminder.description} onChange={(e) => setNewReminder({ ...newReminder, description: e.target.value })} placeholder="Notes..." className="rounded-sm mt-1" rows={2} />
            </div>
            <Button onClick={handleCreateReminder} className="w-full rounded-sm" data-testid="save-reminder-btn">Create Reminder</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
